import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  MapPin, 
  TrendingUp, 
  BarChart3, 
  Sparkles, 
  ChevronRight,
  RefreshCw,
  Database,
  Clock
} from 'lucide-react';
import { useStateContext } from '@/context/StateContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// State flag/icon colors
const STATE_COLORS = {
  WA: { bg: 'from-emerald-500 to-green-600', icon: '🌲' },
  CO: { bg: 'from-blue-500 to-indigo-600', icon: '🏔️' },
  FL: { bg: 'from-orange-400 to-amber-500', icon: '🌴' },
  SC: { bg: 'from-indigo-500 to-purple-600', icon: '🌙' },
  PA: { bg: 'from-yellow-500 to-amber-600', icon: '🔔' },
  OR: { bg: 'from-teal-500 to-cyan-600', icon: '🌊' }
};

export const Home = () => {
  const navigate = useNavigate();
  const { states, loading: contextLoading, selectState } = useStateContext();
  const [statesData, setStatesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ingesting, setIngesting] = useState({});

  useEffect(() => {
    fetchStatesData();
  }, []);

  const fetchStatesData = async () => {
    try {
      const response = await axios.get(`${API}/states`);
      if (response.data.success) {
        setStatesData(response.data.states);
      }
    } catch (error) {
      console.error('Failed to fetch states:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStateClick = (stateCode) => {
    selectState(stateCode);
    navigate(`/${stateCode.toLowerCase()}`);
  };

  const handleIngest = async (stateCode, e) => {
    e.stopPropagation();
    setIngesting(prev => ({ ...prev, [stateCode]: true }));
    
    try {
      const response = await axios.post(`${API}/states/${stateCode}/ingest`);
      if (response.data.success) {
        toast.success(`${stateCode}: Ingested ${response.data.games_saved} games`);
        fetchStatesData(); // Refresh data
      } else {
        toast.error(`${stateCode}: ${response.data.message}`);
      }
    } catch (error) {
      toast.error(`Failed to ingest ${stateCode} data`);
    } finally {
      setIngesting(prev => ({ ...prev, [stateCode]: false }));
    }
  };

  if (loading || contextLoading) {
    return (
      <div className="p-6 md:p-10 space-y-8">
        <div className="text-center space-y-4">
          <Skeleton className="h-12 w-64 mx-auto" />
          <Skeleton className="h-6 w-96 mx-auto" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} className="h-48 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-120px)] p-6 md:p-10" data-testid="home-page">
      {/* Hero Section */}
      <div className="text-center space-y-4 mb-12">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
            Scratch <span className="text-primary">Intel</span>
          </h1>
        </div>
        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
          Data-driven lottery scratch ticket analysis. Find the best value games 
          with real expected value calculations and AI-powered insights.
        </p>
        <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground pt-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            <span>EV Calculations</span>
          </div>
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-primary" />
            <span>Prize Tier Analysis</span>
          </div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <span>AI Insights</span>
          </div>
        </div>
      </div>

      {/* Select Your State */}
      <div className="max-w-6xl mx-auto">
        <h2 className="text-2xl font-semibold mb-6 text-center">
          Select Your State
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {statesData.map((state) => {
            const colors = STATE_COLORS[state.state_code] || { bg: 'from-gray-500 to-gray-600', icon: '🎫' };
            const hasData = state.game_count > 0;
            
            return (
              <Card 
                key={state.state_code}
                className={`relative overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-xl ${
                  hasData ? 'border-border' : 'border-dashed border-muted-foreground/30'
                }`}
                onClick={() => hasData && handleStateClick(state.state_code)}
                data-testid={`state-card-${state.state_code}`}
              >
                {/* Gradient header */}
                <div className={`h-24 bg-gradient-to-r ${colors.bg} flex items-center justify-center`}>
                  <span className="text-5xl">{colors.icon}</span>
                </div>
                
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl">{state.state_name}</CardTitle>
                    <Badge variant={hasData ? "default" : "secondary"}>
                      {state.state_code}
                    </Badge>
                  </div>
                  <CardDescription>
                    {hasData 
                      ? `${state.game_count} scratch games available`
                      : 'Data not yet loaded'
                    }
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="pt-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Database className="w-4 h-4" />
                        <span>{state.game_count} games</span>
                      </div>
                      {state.scratchodds_available && (
                        <Badge variant="outline" className="text-xs">
                          ScratchOdds
                        </Badge>
                      )}
                    </div>
                    
                    {hasData ? (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStateClick(state.state_code);
                        }}
                      >
                        View <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => handleIngest(state.state_code, e)}
                        disabled={ingesting[state.state_code]}
                      >
                        {ingesting[state.state_code] ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Load Data
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info Section */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-card/50">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Expected Value (EV)</h3>
              <p className="text-sm text-muted-foreground">
                We calculate the true expected return on every game based on remaining prizes and tickets.
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-card/50">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Top 3 Prize Tiers</h3>
              <p className="text-sm text-muted-foreground">
                Forget jackpots. We focus on realistic $100-$10K prizes you can actually win.
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-card/50">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">AI-Powered Insights</h3>
              <p className="text-sm text-muted-foreground">
                GPT-5.2 analyzes market trends and alerts you to the best opportunities daily.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Home;
